import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, CircularProgress, Stack } from '@mui/material';
import ShadowAIDiscovery from './components/ShadowAIDiscoveryCard';
import NoCustomTemplateCard from './components/noCustomTemplateCard';
import PageHeader from 'shared/components/PageHeader';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useNavigate } from 'react-router-dom';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import List from './components/List';
import { Toast } from 'shared/helpers';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

const AssessmentTemplates = (): ReactElement => {
  const navigate = useNavigate();
  const riskAssessmentPagination = useSelector(
    (state: IAppState) => state.paginationState.riskAssessment
  );

  const dispatch = useDispatch();

  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const fetchTemplates = (): void => {
    const queryString = new URLSearchParams({
      offset:
        riskAssessmentPagination.offset === 0
          ? String(riskAssessmentPagination.offset + 1)
          : String(riskAssessmentPagination.offset),
      limit: String(riskAssessmentPagination.pageLimit),
    }).toString();
    setIsLoading(true);
    RiskAssessmentService.getCustomAssessmentTemplates(queryString)
      .then((response: any) => {
        const { pagination } = response.data.data;
        setTemplates(response.data.data.templates);
        dispatch(
          PaginationActions.setPagination({
            riskAssessment: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch((error: any) => {
        console.error('Error fetching templates:', error);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [
    riskAssessmentPagination.offset,
    riskAssessmentPagination.pageLimit,
    riskAssessmentPagination.totalPages,
  ]);

  const handleMarkInactive = (id: string): void => {
    setIsLoading(true);
    RiskAssessmentService.deleteTemplate(id)
      .then((response: any) => {
        if (response.data) {
          Toast.showSuccessMessage(response.msg);
          fetchTemplates();
        }
      })
      .catch((error: any) => {
        console.error('Error deleting templates:', error);
      })
      .finally(() => setIsLoading(false));
  };

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        riskAssessment: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        riskAssessment: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  return (
    <>
      <PageHeader
        title="Risk Assessment Templates"
        description=""
        actions={
          <Stack direction="row" spacing={2}>
            {orgACLConfig.riskManagement?.includes(
              'riskManagement.assessmentTemplates.runRiskAssessments'
            ) && (
              <AppPrimaryButton
                startIcon={<PlayCircleOutlineIcon />}
                className="btn-small"
                title="Run Risk Assessments"
                onClick={() => {
                  navigate('/risk-assessments');
                }}
              />
            )}

            {orgACLConfig.riskManagement?.includes(
              'riskManagement.assessmentTemplates.createNewTemplate'
            ) && (
              <AppPrimaryButton
                className="btn-small"
                title="Create New Template"
                onClick={() => navigate('/assessment-templates/add')}
              />
            )}
          </Stack>
        }
      />
      <Box className="risk-management risk-assessment-template mt-5">
        <HeadTag title="Assessment Templates" />
        <Box className="assessment-template-wrapper">
          {false && <ShadowAIDiscovery />}
          {isLoading && (
            <Box className="empty-content">
              <CircularProgress />
            </Box>
          )}
          {!isLoading && (
            <>
              {templates?.length === 0 && <NoCustomTemplateCard />}{' '}
              {templates.length > 0 && (
                <List
                  templates={templates}
                  handlePageChange={handlePageChange}
                  handleRowsPerPageChange={handleRowsPerPageChange}
                  onMarkInactive={(id) => handleMarkInactive(id)}
                  riskAssessmentPagination={riskAssessmentPagination}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AssessmentTemplates;
